import { useEffect, useRef } from 'react';

import { Dialog, IconButton } from '@mui/material';
import { CloseIcon } from '@truvhq/ui';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useLocation } from 'react-router-dom';

import { useGetBillingProfile } from 'entities/billing/api/useGetBillingProfile';
import { useOnboardingState } from 'entities/onboarding/api/useOnboardingState';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { OnboardingConversation } from 'features/onboarding/ui/OnboardingConversation';
import { useAuth } from 'shared/lib/auth/useAuth';
import { FeatureFlag } from 'shared/lib/features/common';
import { localStorageService, StorageKeys } from 'shared/lib/storage/storageService';
import { urls } from 'shared/urls';

// Ids of users we've already auto-opened for, persisted so it happens only once ever (not on every
// refresh/session); afterwards the user opens it manually via the launcher.
function hasAutoOpened(userId: string): boolean {
  const seen = localStorageService.get(StorageKeys.ONBOARDING_AUTO_OPENED);
  return Array.isArray(seen) && seen.includes(userId);
}

function markAutoOpened(userId: string): void {
  const seen = localStorageService.get(StorageKeys.ONBOARDING_AUTO_OPENED);
  const ids = Array.isArray(seen) ? seen : [];
  if (!ids.includes(userId)) {
    localStorageService.set(StorageKeys.ONBOARDING_AUTO_OPENED, [...ids, userId]);
  }
}

// Mounts the conversational onboarding as a global popup driven by useOnboardingDialogStore.
// Auto-opens exactly once per user (persisted in localStorage) for authenticated users in the
// assistant rollout; after that the user opens it manually via the launcher.
export function OnboardingDialog() {
  const { user } = useAuth();
  const assistantEnabled = useFeatureFlagEnabled(FeatureFlag.AI_ASSISTANT);
  const isOpen = useOnboardingDialogStore((state) => state.isOpen);
  const sessionKey = useOnboardingDialogStore((state) => state.sessionKey);
  const open = useOnboardingDialogStore((state) => state.open);
  const close = useOnboardingDialogStore((state) => state.close);
  const autoOpenedRef = useRef(false);

  // Self-serve clients get nudged through the post-signup subscription wizard first (see the
  // Auth0Context redirect, gated on the same can_subscribe signal). We don't open the assistant on
  // top of that wizard; instead we open it once the user *leaves* the wizard the first time — whether
  // they finished all steps or exited early. Other account types have no such wizard, so they open on
  // first load as before.
  const isSelfServe = Boolean(user?.isCompanySelfServe);
  const { data: billingProfile } = useGetBillingProfile({ enabled: assistantEnabled && !!user && isSelfServe });
  const subscriptionWizardPending = isSelfServe && billingProfile?.can_subscribe !== false;

  const onSubscriptionWizard = useLocation().pathname === urls.subscriptionFlow.url();
  const visitedWizardRef = useRef(false);
  if (onSubscriptionWizard) {
    visitedWizardRef.current = true;
  }

  useEffect(() => {
    if (autoOpenedRef.current || !user || !assistantEnabled || typeof window === 'undefined') {
      return;
    }
    // Never while the user is still in the wizard.
    if (onSubscriptionWizard) {
      return;
    }
    // A self-serve user who still owes the wizard but hasn't entered it yet is about to be
    // redirected there — hold off until they've been through and left it.
    if (subscriptionWizardPending && !visitedWizardRef.current) {
      return;
    }
    if (hasAutoOpened(user.id)) {
      return;
    }
    markAutoOpened(user.id);
    autoOpenedRef.current = true;
    open();
  }, [user, assistantEnabled, onSubscriptionWizard, subscriptionWizardPending, open]);

  // Render mode comes from constance (via the onboarding state endpoint): "docked" is a right-side
  // panel that leaves the dashboard interactive; "window" is the centered dialog. Preload it (not
  // gated on isOpen) so the layout is known before the dialog opens — otherwise a docked chat would
  // briefly flash the window mode while the request resolved.
  const { data: onboardingState, isLoading: isLayoutLoading } = useOnboardingState({
    enabled: Boolean(assistantEnabled) && !!user,
  });
  const isDocked = onboardingState?.layout === 'docked';

  const closeButton = (
    <IconButton
      aria-label="Close onboarding"
      onClick={close}
      sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: 'text.secondary' }}
    >
      <CloseIcon />
    </IconButton>
  );
  const conversation = (
    <div className="h-full overflow-hidden">
      <OnboardingConversation key={sessionKey} />
    </div>
  );

  // Hold off rendering a container until the layout is resolved, so a docked chat never flashes the
  // window mode first. Preloaded above, so this is settled before the user can open the dialog.
  if (isLayoutLoading) {
    return null;
  }

  if (isDocked) {
    return (
      isOpen && (
        <div
          role="dialog"
          aria-label="Get started"
          className="fixed inset-y-0 right-0 z-[1300] flex w-[min(30rem,100vw)] flex-col overflow-hidden border-grey30 border-l bg-white shadow-2xl"
        >
          {closeButton}
          {conversation}
        </div>
      )
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      aria-label="Get started"
      maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 720, height: '85vh', borderRadius: '12px' } }}
    >
      {closeButton}
      {conversation}
    </Dialog>
  );
}
